import { type CSSProperties, type HTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import './Skeleton.css';

export type SkeletonShape = 'line' | 'circle' | 'pill' | 'square' | 'block' | 'card';

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  /** Width — number = px, string = any CSS length. Wins over `style.width`;
   *  omitted → `style.width`, else the CSS default (fills a block parent). */
  width?: number | string;
  /** Height — number = px, string = any CSS length. Wins over `style.height`;
   *  omitted → `style.height`, else the CSS min-height (0.8em). */
  height?: number | string;
  /** Shape preset (controls border-radius). Default `line`. */
  shape?: SkeletonShape;
  ref?: Ref<HTMLSpanElement>;
}

/** number = px, string = any CSS length. */
const cssLength = (value: number | string) => (typeof value === 'number' ? `${value}px` : value);

/**
 * Skeleton — shimmer-animated placeholder. Use while a piece of real
 * content is loading to prevent layout shift and signal "something's
 * coming here". Matches the component it replaces in size.
 */
export function Skeleton({
  width,
  height,
  shape = 'line',
  className,
  style,
  ref,
  ...rest
}: SkeletonProps) {
  // Set only what was passed: an omitted prop written as `undefined` would
  // erase the size a caller gave through `style` (the flashcard session's
  // loading card rendered 0px wide).
  const wrapperStyle: CSSProperties = { ...style };
  if (width !== undefined) wrapperStyle.width = cssLength(width);
  if (height !== undefined) wrapperStyle.height = cssLength(height);
  return (
    <span
      ref={ref}
      className={clsx('fluent-skeleton', className)}
      data-shape={shape}
      style={wrapperStyle}
      aria-hidden="true"
      {...rest}
    />
  );
}
